/**
 * Copyright (c) 2021 TechDivision GmbH
 * All rights reserved
 */
import LocalStorageObjectHelper from "./LocalStorageObjectHelper"

/**
 * @class ProductComparisonVariantPicker
 */
class ProductComparisonVariantPicker {

    /**
     * handles the events for the product comparison
     *
     * @constructor
     */
    constructor(comparisonProductColorVariants) {

        this.formSelectedVariantsInput = document.querySelector('#comparison-form-products')

        this.identifierSelector = 'data-product-identifier'
        this.colorCodeSelector = 'data-color-order-code'

        let productWrapperSelector = '.js-product-comparison__product'
        this.productVariantsWrapper = document.querySelectorAll(productWrapperSelector)

        /**
         * The identifier for the product storage
         *
         * @type {LocalStorageObjectHelper}
         */
        this.ProductComparisonDataHelper = new LocalStorageObjectHelper('comparisonProducts')

        this.init()
    }

    /**
     * @method init
     */
    init() {
        if (this.formSelectedVariantsInput) {
            this.bindEvents()
            this.updateInitialColorCodeForProduct()
        } else {
            this.updateInitialColorCodeForProduct()
            this.hideComparisonSelectFields()
        }
    }

    /**
     * Bind all needed events
     *
     * @method bindEvents
     */
    bindEvents() {
        // access the color variants wrapper
        this.productVariantsWrapper.forEach((productWrapper, index) => {
            // access the variants of each product
            const productVariantsFigures = productWrapper.querySelectorAll('.js-product-images--figure')
            // set initial
            this.setInitialColorCodeForProduct(productWrapper, productVariantsFigures[0], index)
            productVariantsFigures.forEach((figure) => {
                figure.dataset.productIdentifier = productWrapper.dataset.productIdentifier
                figure.addEventListener('mouseover', () => {
                    // access product image over combination of js-class selector, data-attr and img tag
                    let productIMG = productWrapper.querySelector(
                        '.js-product__image-wrapper[' +
                        this.identifierSelector +
                        '="' +
                        figure.dataset.productIdentifier +
                        '"] img'
                    )
                    // set Image src
                    productIMG.src = figure.dataset.productImage
                    // save current variant of product at the wrapper
                    productWrapper.dataset.productVariant = figure.dataset.colorOrderCode
                    // set selected variants to form
                    this.buildAndSetColorCodeJsonToForm()
                    // remove selected from all
                    productVariantsFigures.forEach(function (figure) {
                        figure.classList.remove('selected')
                    })
                    // add selected class to current
                    figure.classList.add('selected')
                })
            })
        })
        this.buildAndSetColorCodeJsonToForm()
    }

    /**
     * set initial variants
     *
     * @param productWrapper
     * @param fallbackVariant
     * @param index
     */
    setInitialColorCodeForProduct(productWrapper, fallbackVariant, index) {
        // primary try to get selected variants over form input to catch form errors (validation failed, ...)
        if (this.formSelectedVariantsInput && this.formSelectedVariantsInput.value) {
            // get all selected products with variants
            let selectedVariants = JSON.parse(this.formSelectedVariantsInput.value)

            // set selected variant to product
            productWrapper.dataset.productVariant = selectedVariants[index].code

            // get selected Variant/Color through selectedVariants[index] from form
            let selectedVariant = productWrapper.querySelector(
                '.js-product-images--figure[' + this.colorCodeSelector + '="' + selectedVariants[index].code + '"]'
            )

            // get image element for selected product
            let productIMG = productWrapper.querySelector(
                '.js-product__image-wrapper[' +
                this.identifierSelector +
                '="' +
                productWrapper.dataset.productIdentifier +
                '"] img'
            )
            // set product img by selected variant image
            productIMG.src = selectedVariant.dataset.productImage

            // add selected for visual feedback of selection
            selectedVariant.classList.add('selected')
        } else {
            //try to set image over localstorage
            let setOverLocalStorage = false

            // get previously products with variants by local storage
            let previouslySelectedVariants = this.ProductComparisonDataHelper.data

            if (previouslySelectedVariants.length) {
                // find occurrence of product within previously products
                let element = previouslySelectedVariants[index]
                if (element) {
                    // check if it's the correct product
                    if (element.name == productWrapper.dataset.productTitle && element.code) {
                        let selectedVariant = productWrapper.querySelector(
                            '.js-product-images--figure[' + this.colorCodeSelector + '="' + element.code + '"]'
                        )
                        if (selectedVariant) {
                            // set selected variant to product
                            productWrapper.dataset.productVariant = element.code
                            // get image element for selected product
                            let productIMG = productWrapper.querySelector(
                                '.js-product__image-wrapper[' +
                                this.identifierSelector +
                                '="' +
                                productWrapper.dataset.productIdentifier +
                                '"] img'
                            )
                            // set product img by selected variant image
                            productIMG.src = selectedVariant.dataset.productImage
                            // add selected for visual feedback of selection
                            selectedVariant.classList.add('selected')
                            // set flag
                            setOverLocalStorage = true
                        }
                    }
                }
            }

            // if none is found use first variant as selected
            if (!setOverLocalStorage) {
                productWrapper.dataset.productVariant = fallbackVariant.dataset.colorOrderCode
                fallbackVariant.classList.add('selected')
            }
        }
    }

    /**
     * Update the initial variant
     * @method updateInitialColorCodeForProduct
     */
    updateInitialColorCodeForProduct() {
        this.productVariantsWrapper.forEach((productVariants, index) => {
            // access the variants of each product
            const productVariantsFigures = productVariants.querySelectorAll('.js-product-images--figure')
            // set initial
            this.setInitialColorCodeForProduct(productVariants, productVariantsFigures[0], index)
        })
    }


    /**
     * Hide selectFields and headlines after comparisonForm has been sent
     * @method hideComparisonSelectFields
     */
    hideComparisonSelectFields() {
        // Hide select Fields
        document.querySelectorAll('.js-product-comparison--select').forEach( (selectField) => {
            selectField.style = 'display: none;'
        })
        // Hide field headlines
        document.querySelectorAll('.product-comparison__add-third-product').forEach( (selectField) => {
            selectField.style = 'display: none;'
        })
    }

    /**
     * @method buildAndSetColorCodeJsonToForm
     * @returns {json}
     */
    buildAndSetColorCodeJsonToForm() {
        let data = []

        this.productVariantsWrapper.forEach(function (item) {
            let name = item.dataset.productTitle
            let productIdentifier = item.dataset.productIdentifier
            let code = item.dataset.productVariant
            data.push({name: name, code: code, productIdentifier: productIdentifier})
        })

        // set form value
        this.formSelectedVariantsInput.value = JSON.stringify(data)

        // set local storage TODO if color codes are given by detail page then we do not need this overwrite
        this.ProductComparisonDataHelper.overwrite(data)

        // Triggers ProductComparisonDownload to change the target for the pdf download
        $(this.formSelectedVariantsInput).trigger('selectedColorsChanged', [data])
    }


}

function initProductComparisonVariantPicker() {
    let comparisonProductColorVariants = document.querySelectorAll('.js-product-comparison__color-variants')
    if (comparisonProductColorVariants && comparisonProductColorVariants.length) {
        new ProductComparisonVariantPicker()
    }
}

// If the script is initialized after the document is already loaded, initialize modules instantly
if (document.readyState !== 'loading') {
    initProductComparisonVariantPicker()
} else {
    // else add an event listener for when the document is fully loaded
    document.addEventListener('DOMContentLoaded', initProductComparisonVariantPicker)
}
